import { useEffect, useState } from 'react';
import { Drawer, DrawerContent } from '@/components/ui/drawer';
import { Icon } from '@/components/ui/icon';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { ProgressDots } from '@/components/shared/progressDots';
import { useClientTranslation } from '@/lib/core/i18n';
import { cn } from '@/lib/core/utils/cn';
import type { MewsCategory } from '@/services/platform/data/booking/mews/types';
import type { MewsWhichSheetProps, MewsWhichPageProps, CategoryRowProps } from '../types';
import { SUNBED_LABELS } from '../../labels';

// ── Category row ──────────────────────────────────────────────────────────────

function CategoryRow({
  item,
  isExpanded,
  onToggleExpand,
  labels = SUNBED_LABELS,
  showPrice = true,
  defaultTag,
  showDivider = true,
}: CategoryRowProps) {
  const t = useClientTranslation();
  const disabled = !item.availableUnits;
  const tag = item.tag ?? defaultTag;

  // Fall back to no image (same as a missing `item.image`) if it fails to load.
  const [imageFailed, setImageFailed] = useState(false);
  useEffect(() => setImageFailed(false), [item.image]);
  const showImage = !!item.image && !imageFailed;

  return (
    <div className="flex flex-col py-4">
      {/* Top row: radio + image + copy */}
      <label
        htmlFor={item.id}
        className={cn(
          'flex w-full items-center gap-4 text-left',
          disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
        )}
      >
        {/* Radio — stays centred against the row while the image and copy stretch beside it. */}
        <RadioGroupItem id={item.id} value={item.id} disabled={disabled} />

        {/* Image + copy. `items-stretch` is what gives the image its height: the column is 64px
            wide by design, but how tall it runs is decided by the copy next to it, so the two
            always end flush. The image is positioned rather than sized in percentages — a
            stretched flex item has no height for `h-full` to resolve against until layout is
            settled, and `inset-0` sidesteps that. */}
        <div className="flex min-w-0 flex-1 items-stretch gap-4">
          {showImage && (
            <div className="relative w-16 shrink-0 overflow-hidden">
              <img
                src={item.image}
                alt={item.name}
                className="absolute inset-0 size-full object-cover"
                loading="lazy"
                onError={() => setImageFailed(true)}
              />
            </div>
          )}

          {/* Copy details */}
          <div className="flex min-w-0 flex-1 flex-col gap-2">
            {tag && (
              <p className="font-pp-neue-montreal text-jncquoi-neutral-700 text-[10px] font-semibold tracking-[0.5px] uppercase">
                {tag}
              </p>
            )}
            <p className="font-inter text-jncquoi-neutral-1000 truncate text-[16px] font-semibold tracking-[0.25px]">
              {item.name}
            </p>
            <div className="font-pp-neue-montreal text-jncquoi-neutral-700 flex flex-col text-[12px] tracking-[0.5px]">
              {item.maxPersons && <span>{t(labels.maxPersons, { value: item.maxPersons })}</span>}
              {item.availableUnits !== undefined && (
                <span>{t(labels.availableUnits, { count: item.availableUnits })}</span>
              )}
            </div>
          </div>
        </div>
      </label>

      {/* Bottom row: price + view more — indented past radio (20px) + gap (16px) = pl-9 */}
      <div
        className={cn(
          'mt-4 flex flex-col gap-4 pb-4 pl-9',
          showDivider && 'border-jncquoi-neutral-200 border-b-[0.5px]'
        )}
      >
        {/* Without a price there is nothing on the left, so the disclosure keeps to the right edge
            rather than sliding across to where the price used to be. */}
        <div className={cn('flex items-center', showPrice ? 'justify-between' : 'justify-end')}>
          {showPrice && (
            <p className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[14px] font-semibold tracking-[0.5px]">
              {item.pricePerDay} €{' '}
              <span className="text-[12px] font-normal">{item.priceUnit ?? t(labels.priceUnit)}</span>
            </p>
          )}
          {item.description && (
            <button
              type="button"
              onClick={onToggleExpand}
              className="font-pp-neue-montreal text-jncquoi-neutral-700 flex cursor-pointer items-center gap-1 text-[12px] tracking-[0.5px]"
            >
              {t(labels.viewMoreDetails)}
              <Icon
                name="nav-arrow-down"
                width={16}
                height={16}
                strokeWidth={1.5}
                className={cn(
                  'text-jncquoi-neutral-700 shrink-0 transition-transform',
                  isExpanded && 'rotate-180'
                )}
              />
            </button>
          )}
        </div>

        {isExpanded && item.description && (
          <p className="font-pp-neue-montreal text-jncquoi-neutral-700 text-[12px] tracking-[0.5px]">
            {item.description}
          </p>
        )}
      </div>
    </div>
  );
}

// ── Sheet ─────────────────────────────────────────────────────────────────────

export function MewsWhichSheet({
  isOpen,
  onClose,
  cmsProps,
  categories,
  selected,
  onSelect,
  labels = SUNBED_LABELS,
  showPrice,
  defaultTag,
  showDivider,
}: MewsWhichSheetProps) {
  const t = useClientTranslation();
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const title = cmsProps?.title ?? t(labels.whichTitle);
  const subtitle = cmsProps?.subtitle ?? t(labels.whichSubtitle);
  const sectionLabel = cmsProps?.categoriesAvailableLabel ?? t(labels.categoriesAvailable);

  return (
    <Drawer
      open={isOpen}
      onOpenChange={open => {
        if (!open) onClose();
      }}
    >
      <DrawerContent className="border-jncquoi-neutral-200 h-[80dvh] min-h-[min(650px,100dvh)] border-t-[0.5px]">
        {/* Header */}
        <div className="flex shrink-0 flex-col gap-2 px-4 pt-8">
          <div className="flex items-center gap-8">
            <h2 className="font-pp-neue-montreal flex-1 text-[32px] font-medium tracking-[0.5px] text-[#2a2a2a]">
              {title}
            </h2>
            <button
              type="button"
              onClick={onClose}
              className="flex size-11 cursor-pointer items-center justify-center rounded-full"
              aria-label={t('App.Labels.Close')}
            >
              <Icon
                name="xmark"
                width={24}
                height={24}
                strokeWidth={1.5}
                className="text-jncquoi-neutral-1000"
              />
            </button>
          </div>
          <p className="font-pp-neue-montreal text-[16px] tracking-[0.25px] text-[#707070]">{subtitle}</p>
        </div>

        {/* Scrollable list */}
        <div data-base-ui-swipe-ignore className="flex-1 overflow-y-auto">
          <div className="px-4 pt-6 pb-8">
            <p className="font-pp-neue-montreal text-jncquoi-neutral-800 mb-2 text-[14px] tracking-[0.5px] uppercase">
              {sectionLabel}
            </p>
            <RadioGroup
              value={selected?.id ?? ''}
              onValueChange={id => {
                const item = categories.find(c => c.id === id);
                if (item) {
                  onSelect(item);
                  onClose();
                }
              }}
            >
              {categories.map(item => (
                <CategoryRow
                  labels={labels}
                  key={item.id}
                  item={item}
                  isExpanded={expandedId === item.id}
                  onToggleExpand={() => setExpandedId(prev => (prev === item.id ? null : item.id))}
                  showPrice={showPrice}
                  defaultTag={defaultTag}
                  showDivider={showDivider}
                />
              ))}
            </RadioGroup>
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
}

// ── MewsWhichPage (full-page step) ──────────────────────────────────────────

export function MewsWhichPage({
  cmsProps,
  showRateLabel,
  categories,
  selected,
  onContinue,
  onBack,
  isLoading,
  step,
  whenLabel,
  labels = SUNBED_LABELS,
  showPrice,
  defaultTag,
  showDivider,
}: MewsWhichPageProps) {
  const t = useClientTranslation();
  const [localSelected, setLocalSelected] = useState<MewsCategory | null>(selected);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const title = cmsProps?.title ?? t(labels.whichTitle);
  const subtitle = cmsProps?.subtitle ?? t(labels.whichSubtitle);
  const sectionLabel = cmsProps?.categoriesAvailableLabel ?? t(labels.categoriesAvailable);

  return (
    <>
      {/* Header */}
      <header className="flex shrink-0 items-center justify-between px-4 py-2">
        <button
          type="button"
          aria-label={t('App.Labels.Back')}
          className="flex size-11 cursor-pointer items-center justify-center"
          onClick={onBack}
        >
          <Icon
            name="long-arrow-up-left"
            width={24}
            height={24}
            strokeWidth={2}
            className="text-jncquoi-neutral-1000"
          />
        </button>
        <ProgressDots step={step} />
      </header>

      {/* Header copy */}
      <div className="shrink-0 px-4 pt-6 pb-2">
        <h2 className="font-pp-neue-montreal text-[32px] font-medium tracking-[0.5px] text-[#2a2a2a]">
          {title}
        </h2>
        <p className="font-pp-neue-montreal mt-2 text-[16px] tracking-[0.25px] text-[#707070]">{subtitle}</p>
        {whenLabel && (
          <p className="font-pp-neue-montreal text-jncquoi-neutral-1000 mt-2 text-[16px] font-medium tracking-[0.25px]">
            {whenLabel}
          </p>
        )}
      </div>

      {/* Scrollable category list */}
      <div className="flex flex-1 flex-col overflow-y-auto px-4 pb-28">
        <p className="font-pp-neue-montreal text-jncquoi-neutral-800 mt-6 mb-2 text-[14px] tracking-[0.5px] uppercase">
          {sectionLabel}
        </p>
        <RadioGroup
          value={localSelected?.id ?? ''}
          onValueChange={id => setLocalSelected(categories.find(c => c.id === id) ?? null)}
        >
          {categories.map(item => (
            <CategoryRow
              labels={labels}
              key={item.id}
              item={item}
              isExpanded={expandedId === item.id}
              onToggleExpand={() => setExpandedId(prev => (prev === item.id ? null : item.id))}
              showPrice={showPrice}
              defaultTag={defaultTag}
              showDivider={showDivider}
            />
          ))}
        </RadioGroup>
      </div>

      {/* Sticky Show Rate button */}
      <div className="fixed bottom-0 left-0 w-full border-t-[0.5px] border-[#c8c8c8] bg-white px-4 pt-6 pb-10.5">
        <button
          type="button"
          disabled={!localSelected || isLoading}
          onClick={() => localSelected && onContinue(localSelected)}
          className="font-pp-neue-montreal enabled:bg-jncquoi-neutral-1000 disabled:bg-jncquoi-neutral-600 flex h-13 w-full cursor-pointer items-center justify-center rounded-[2px] text-[12px] font-semibold tracking-[0.5px] text-white uppercase"
        >
          {showRateLabel ?? t(labels.showRate)}
        </button>
      </div>
    </>
  );
}
